import { format } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';

import { noshowAlertsAPI } from '@/services/noshowAlertsAPI';
import { NoshowAlertDetail, ResolutionType } from '@/types/noshowAlerts';

import Alert from '@/components/Alert';
import Card from '@/components/Card';
import LoadingState from '@/components/LoadingState';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Caption } from '@/components/Typography';
import styled from '@/styles';

interface AlertDetailsModalProps {
  alertId: number;
  onClose: () => void;
}

const RESOLUTION_LABELS: Record<ResolutionType, string> = {
  no_response: 'No Response',
  backfill_created: 'Backfill Created',
  backfill_declined: 'Backfill Declined',
  other: 'Other',
};

const Row = styled('div', {
  display: 'grid',
  gridTemplateColumns: '140px minmax(0, 1fr)',
  gap: '12px',
  alignItems: 'start',
});

const NotesBlock = styled('div', {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
});

const Divider = styled('div', {
  borderTop: '1px solid $neutralLighter',
  marginTop: '4px',
  paddingTop: '12px',
});

const formatDateTime = (value: string | null) =>
  value ? format(new Date(value), 'MMM dd, yyyy h:mm a') : '—';

const DetailRow = ({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) => (
  <Row>
    <Caption color="lighter">{label}</Caption>
    {children}
  </Row>
);

const AlertDetailsModal = ({ alertId, onClose }: AlertDetailsModalProps) => {
  const [alert, setAlert] = useState<NoshowAlertDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadAlert = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await noshowAlertsAPI.getAlert(alertId);
      if (response.success) {
        setAlert(response.data);
      }
    } catch (err: any) {
      console.error('[AlertDetailsModal] Error:', err);
      setError('Failed to load alert details');
    } finally {
      setLoading(false);
    }
  }, [alertId]);

  useEffect(() => {
    loadAlert();
  }, [loadAlert]);

  const isResolved = Boolean(alert?.resolved_at);

  return (
    <Modal onRequestClose={onClose} title={`Alert #${alertId}`}>
      <Card.Section>
        {error && (
          <Alert
            description={error}
            icon="warning"
            status="danger"
            title="Error"
          />
        )}
        {loading && <LoadingState overlayColor="white" />}
        {!loading && alert && (
          <Stack vertical gap={12}>
            <DetailRow label="Worker">
              <Body weight="medium">{alert.worker?.name || 'N/A'}</Body>
            </DetailRow>
            <DetailRow label="Supervisor">
              <Body weight="medium">{alert.supervisor?.name || 'N/A'}</Body>
            </DetailRow>
            <DetailRow label="Client">
              <Body weight="medium">{alert.customer_name || 'N/A'}</Body>
            </DetailRow>
            <DetailRow label="Shift">
              <Body>
                {alert.shift_start_at
                  ? `${formatDateTime(alert.shift_start_at)} – ${formatDateTime(
                      alert.shift_end_at,
                    )}`
                  : '—'}
              </Body>
            </DetailRow>
            <DetailRow label="Alert raised">
              <Body>{formatDateTime(alert.created_at)}</Body>
            </DetailRow>

            <Divider>
              <Stack vertical gap={12}>
                <Body weight="medium">Resolution</Body>
                {!isResolved && (
                  <Body color="light">This alert has not been resolved yet.</Body>
                )}
                {isResolved && (
                  <>
                    <DetailRow label="Type">
                      <Body weight="medium">
                        {alert.resolution_type
                          ? RESOLUTION_LABELS[alert.resolution_type] ||
                            alert.resolution_type
                          : '—'}
                      </Body>
                    </DetailRow>
                    <DetailRow label="Resolved">
                      <Body>
                        {formatDateTime(alert.resolved_at)}
                        {alert.resolved_by ? ` by ${alert.resolved_by}` : ''}
                      </Body>
                    </DetailRow>
                    {alert.backfill_job_url && (
                      <DetailRow label="Backfill job">
                        <Body>
                          <a
                            href={alert.backfill_job_url}
                            rel="noreferrer"
                            target="_blank"
                          >
                            {alert.backfill_job_url}
                          </a>
                        </Body>
                      </DetailRow>
                    )}
                    <DetailRow label="Notes">
                      {alert.notes ? (
                        <NotesBlock>
                          <Body>{alert.notes}</Body>
                        </NotesBlock>
                      ) : (
                        <Body color="light">—</Body>
                      )}
                    </DetailRow>
                  </>
                )}
              </Stack>
            </Divider>
          </Stack>
        )}
      </Card.Section>
    </Modal>
  );
};

export default AlertDetailsModal;
